// ImageOverlay.jsx
// Component that shows an image overlay
// The image is clickable and can be expanded to show the full image.
// The image can also be opened in a new window.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// Material UI imports
import { makeStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Tooltip from "@mui/material/Tooltip";

// HSA imports
import ValidatedImage from "./ValidatedImage";
import Backend from "../../common/utils/Backend";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: 130,
    border: "1px solid #ccc",
    cursor: "pointer",
    marginBottom: 6,
  },
  img: {
    width: "100%",
  },
  indicator: {
    display: "flex",
    gap: 4,
    alignItems: "center",
    justifyContent: "flex-end",
  },
  iconButton: {
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.55)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    border: "1px solid rgba(255,255,255,0.2)",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.7)",
    },
  },
}));

const ImageOverlay = (props) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);
  const [cacheBust, setCacheBust] = React.useState(0);
  const [needsRotate, setNeedsRotate] = React.useState(false); // frontend-only rotation (macro overlay -> vertical)
  const containerRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  const imageUrl = props.imageUrl;
  const buttonsBelow = !!props.buttonsBelow; // when true, render icon buttons under the image
  const isMacroImage = React.useMemo(() => {
    try {
      const url = new URL(imageUrl, window.location.origin);
      const at = url.searchParams.get("attachmentType") || "";
      return at === "macroImage";
    } catch {
      return false;
    }
  }, [imageUrl]);

  /**
   * Opens the dialog to show the enlarged image.
   */
  const handleImageClick = () => {
    setDialogOpen(true);
  };

  /**
   * Closes the dialog by setting the dialog open state to false.
   */
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Rotate image on backend by the given angle and refresh the cached asset
  const handleRotate = async (e, angle = 90) => {
    e.stopPropagation();
    try {
      const url = new URL(imageUrl, window.location.origin);
      // Only supported for slide attachments
      if (!url.pathname.includes("/api/rendering/render_slide_attachment")) {
        return;
      }
      const id = url.searchParams.get("id");
      const attachmentType = url.searchParams.get("attachmentType") || "label";
      await Backend.rotateSlideAttachment(id, attachmentType, angle);
      // Bust browser cache so <img> reloads the updated cached file
      setCacheBust(Date.now());
      // Notify other UI parts (e.g., SlidePreview) that an attachment rotated
      try {
        window.dispatchEvent(
          new CustomEvent("hsa:slide-attachment-rotated", {
            detail: { id, attachmentType, angle, ts: Date.now() },
          })
        );
      } catch (e) {
        // no-op
      }
    } catch (err) {
      console.error("Rotate failed", err);
      if (window.openErrorDialog)
        window.openErrorDialog("Rotation failed: " + err.message);
    }
  };

  const bustedUrl = React.useMemo(() => {
    try {
      const url = new URL(imageUrl, window.location.origin);
      if (cacheBust) url.searchParams.set("cb", String(cacheBust));
      return url.toString();
    } catch {
      return (
        imageUrl + (imageUrl.includes("?") ? "&" : "?") + `cb=${cacheBust}`
      );
    }
  }, [imageUrl, cacheBust]);

  // Add this function to handle opening the image in a new window
  const handleOpenInNew = (e) => {
    e.stopPropagation(); // Prevent triggering handleImageClick
    const width = 800;
    const height = 600;
    const features = `width=${width},height=${height},toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes`;
    window.open(bustedUrl, "_blank", features);
  };

  // Small helper to render a 90° clockwise rotated image into a canvas, fit to container width
  const drawClockwise90IntoCanvas = React.useCallback(
    (img, container, canvas) => {
      if (!img || !container || !canvas) return false;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      if (w <= 0 || h <= 0) return false;
      const Wc = container.clientWidth || 130; // overlay fixed width; fallback 130
      const dpr = window.devicePixelRatio || 1;
      // After rotation, the displayed width corresponds to original height
      const scale = Wc / h;
      const Hc = Math.max(1, Math.round(scale * w));
      canvas.width = Math.max(1, Math.floor(Wc * dpr));
      canvas.height = Math.max(1, Math.floor(Hc * dpr));
      canvas.style.width = Wc + "px";
      canvas.style.height = Hc + "px";
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.translate(Wc / 2, Hc / 2);
      ctx.rotate(Math.PI / 2); // clockwise 90°
      ctx.scale(scale, scale);
      ctx.drawImage(img, -w / 2, -h / 2);
      ctx.restore();
      return true;
    },
    []
  );

  // Reset rotation state whenever the URL changes (e.g., after cache bust)
  React.useEffect(() => {
    setNeedsRotate(false);
    const c = canvasRef.current;
    if (c) {
      const ctx = c.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, c.width || 0, c.height || 0);
    }
  }, [bustedUrl]);

  return (
    <>
      <div
        style={{
          display: isValid ? "block" : "none",
        }}
        className={classes.root}
        onClick={handleImageClick}
      >
        {!buttonsBelow && (
          <div className={classes.indicator}>
            {/* Remove expand/collapse icons */}
            <IconButton
              size="small"
              onClick={(e) => handleRotate(e, 90)}
              aria-label="Rotate 90°"
              title="Rotate 90°"
              className={classes.iconButton}
            >
              <RotateRightIcon fontSize="small" />
            </IconButton>
            <Tooltip title="Open image in new window">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInNew(e);
                }}
                aria-label="Open image in new window"
                className={classes.iconButton}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}
        <div ref={containerRef}>
          {/* Canvas shown only when a rotation is needed (macro overlay to vertical) */}
          <canvas
            ref={canvasRef}
            style={{
              display: needsRotate ? "block" : "none",
              width: "100%",
              height: "auto",
              borderRadius: 4,
            }}
          />
          <ValidatedImage
            className={classes.img}
            draggable="false"
            src={bustedUrl}
            alt="Slide Image"
            setIsValid={setIsValid}
            style={{ display: needsRotate && isMacroImage ? "none" : "block" }}
            onLoad={(e) => {
              // For macro images in overlay: ensure portrait orientation by rotating landscape images clockwise 90°
              if (!isMacroImage) return;
              try {
                const img = e?.target;
                const w = img?.naturalWidth || 0;
                const h = img?.naturalHeight || 0;
                if (w > 0 && h > 0 && w > h) {
                  const ok = drawClockwise90IntoCanvas(
                    img,
                    containerRef.current,
                    canvasRef.current
                  );
                  if (ok) setNeedsRotate(true);
                } else {
                  setNeedsRotate(false);
                }
              } catch (_) {
                // ignore; fall back to default rendering
              }
            }}
          />
        </div>
        {buttonsBelow && (
          <div className={classes.indicator} style={{ marginTop: 4 }}>
            <IconButton
              size="small"
              onClick={(e) => handleRotate(e, 180)}
              aria-label="Rotate 180°"
              title="Rotate 180°"
              className={classes.iconButton}
            >
              <RotateRightIcon fontSize="small" />
            </IconButton>
            <Tooltip title="Open image in new window">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInNew(e);
                }}
                aria-label="Open image in new window"
                className={classes.iconButton}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md">
        <DialogContent style={{ padding: 0 }}>
          <img
            src={bustedUrl}
            alt="Slide Label"
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              display: "block",
              margin: "0 auto",
            }}
            draggable="false"
            onClick={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

ImageOverlay.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  buttonsBelow: PropTypes.bool,
};

export default ImageOverlay;
